import { IOrgChart } from '@/states/organization.state';
import {
  ORG_DIVISIONS,
  ORG_DIVISION_KEY_SET,
  ORG_LEVELS,
  ORG_LEVEL_KEY_SET,
  OrgDivisionKey,
  OrgLevelKey,
} from '@/utils/constants/organization-matrix';

export interface ITemplateNode {
  id: number;
  template_id: number;
  name: string;
  parent_id: number | null;
  lft: number;
  rgt: number;
}

/**
 * Transforms a flat list of template nodes into a hierarchical IOrgChart structure.
 */
export const buildTreeFromFlatList = (nodes: ITemplateNode[]): IOrgChart | null => {
  if (!nodes || nodes.length === 0) return null;

  const nodeMap = new Map<number, IOrgChart>();
  let root: IOrgChart | null = null;

  // 1. Create IOrgChart objects for all nodes
  nodes.forEach(node => {
    nodeMap.set(node.id, {
      id: node.id,
      name: node.name,
      description: '',
      is_published: true,
      tenant_id: 0, // Placeholder
      parent_id: node.parent_id,
      children: [],
    });
  });

  // 2. Build hierarchy
  nodes.forEach(node => {
    const current = nodeMap.get(node.id);
    if (!current) return;

    // Treat null or 0 as root
    if (node.parent_id === null || node.parent_id === 0) {
      // If we already have a root, this might be a multi-root tree (forest),
      // but for now we only support single root. logic might need adjustment if multiple roots allowed.
      // For safety, just take the first one or the one explicitly null.
      if (!root || node.parent_id === null) {
        root = current;
      }
    } else {
      const parent = nodeMap.get(node.parent_id);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(current);
      } else {
        // If parent not found, it might be a root candidate if the data is partial
        // But strictly it's an orphan.
        // fallback: if no root is found yet, maybe this is it? Unlikely for valid data.
      }
    }
  });

  // Fallback: if no root found (circular or bad data), pick the node with smallest ID or just the first one?
  if (!root && nodes.length > 0) {
    // Try to find a node that is not a child of anyone else in this set
    const childIds = new Set<number>();
    nodes.forEach(n => {
      if (n.parent_id && nodeMap.has(n.parent_id)) {
        childIds.add(n.id);
      }
    });

    const potentialRoots = nodes.filter(n => !childIds.has(n.id));
    if (potentialRoots.length > 0) {
      root = nodeMap.get(potentialRoots[0].id) || null;
    } else {
      // Circular dependency? Just return first.
      root = nodeMap.get(nodes[0].id) || null;
    }
  }

  return root;
};

/**
 * Transforms an IOrgChart node into a React D3 Tree compatible node.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const transformToD3Tree = (node: IOrgChart | null | undefined): any => {
  if (!node?.name) return null;

  return {
    name: node.name || 'Untitled',
    description: node.description || '',
    id: node.id,
    parent_id: node.parent_id,
    division_key: node.division_key,
    level_key: node.level_key,
    is_published: node.is_published,
    children: Array.isArray(node.children) ? node.children.map(transformToD3Tree).filter(Boolean) : [],
  };
};

/**
 * Builds a forest (array of root IOrgChart nodes with nested children)
 * from a flat list, using parent_id to reconstruct hierarchy.
 */
export const buildIOrgChartTree = (nodes: IOrgChart[]): IOrgChart[] => {
  const map = new Map<number, IOrgChart>();
  nodes.forEach(n => map.set(n.id, { ...n, children: [] }));

  const roots: IOrgChart[] = [];
  nodes.forEach(n => {
    const current = map.get(n.id)!;
    if (n.parent_id && map.has(n.parent_id)) {
      const parent = map.get(n.parent_id)!;
      parent.children = parent.children || [];
      parent.children.push(current);
    } else {
      roots.push(current);
    }
  });

  return roots;
};

const DIVISION_LABEL_TO_KEY = ORG_DIVISIONS.reduce<Record<string, OrgDivisionKey>>((acc, item) => {
  acc[item.label.toLowerCase()] = item.key;
  return acc;
}, {});

const LEVEL_LABEL_TO_KEY = ORG_LEVELS.reduce<Record<string, OrgLevelKey>>((acc, item) => {
  acc[item.label.toLowerCase()] = item.key;
  return acc;
}, {});

export const normalizeDivisionKey = (value?: string | null): OrgDivisionKey | null => {
  if (!value) return null;
  const lowered = value.toLowerCase().trim();

  if (ORG_DIVISION_KEY_SET.has(lowered)) {
    return lowered as OrgDivisionKey;
  }

  return DIVISION_LABEL_TO_KEY[lowered] || null;
};

export const normalizeLevelKey = (value?: string | null): OrgLevelKey | null => {
  if (!value) return null;
  const lowered = value.toLowerCase().trim();

  if (ORG_LEVEL_KEY_SET.has(lowered)) {
    return lowered as OrgLevelKey;
  }

  return LEVEL_LABEL_TO_KEY[lowered] || null;
};

export interface GroupedMatrixNode {
  division: OrgDivisionKey;
  level: OrgLevelKey;
  nodes: IOrgChart[];
}

export const groupOrgChartNodesByMatrix = (nodes: IOrgChart[]): GroupedMatrixNode[] => {
  const grouped = new Map<string, IOrgChart[]>();

  nodes.forEach(node => {
    const division = normalizeDivisionKey(node.division_key);
    const level = normalizeLevelKey(node.level_key);
    if (!division || !level) return;

    const matrixKey = `${division}::${level}`;
    const current = grouped.get(matrixKey) || [];
    current.push(node);
    grouped.set(matrixKey, current);
  });

  return ORG_DIVISIONS.flatMap(division =>
    ORG_LEVELS.map(level => {
      const matrixKey = `${division.key}::${level.key}`;
      const nodesAtCell = grouped.get(matrixKey) || [];
      const sorted = [...nodesAtCell].sort((a, b) => {
        const aTime = a.created_at || '';
        const bTime = b.created_at || '';
        return aTime.localeCompare(bTime);
      });

      return {
        division: division.key,
        level: level.key,
        nodes: sorted,
      };
    }),
  );
};

export const hasLegacyOrgChartData = (nodes: IOrgChart[]): boolean => {
  // Root nodes are conceptual containers (e.g. "Sekolah SD", "PT Demo"),
  // not real positions, so they are intentionally allowed to carry NULL
  // division/level metadata and must be excluded from the legacy check.
  // Otherwise applying a template with a generic root would always trip
  // the banner even after a fresh apply.
  return nodes.some(
    node => node.parent_id != null && (!normalizeDivisionKey(node.division_key) || !normalizeLevelKey(node.level_key)),
  );
};
